import { FC } from "react";
import { useLicenses } from "../contexts/licenseContext";
import { Button, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DividerGradient from "./dividerGradient";
import { licensePriceCents } from "../utils/licenseUtils";
import useModal from "../hooks/useModal";
import ConfirmDialog from "./confirmDialog";
import LicenseCheckoutForm from "./licenseCheckoutForm";

const LicenseCart: FC = () => {
	const theme = useTheme();
	const { songsInCart, removeSongFromCart, clearCart } = useLicenses();
	const { show: showModal } = useModal();

	const totalCents = licensePriceCents(songsInCart.length);
	const dollars = Math.floor(totalCents / 100);
	const centsRemainder = (totalCents % 100).toString().padStart(2, '0');

	const handleEmptyCartClick = async () => {
		const confirmed = await showModal(
			360,
			{ prefix: 'fas', iconName: 'times' },
			'Empty Cart?',
			<ConfirmDialog
				message={`This will remove all ${songsInCart.length} song${songsInCart.length !== 1 ? 's' : ''} from your cart. This can't be undone.`}
				confirmLabel="Yes, Empty It"
				cancelLabel="Keep My Selection"
			/>
		);

		if (confirmed === true) {
			clearCart();
		}
	};

	const handleCheckoutClick = () => {
		showModal(
			420,
			{ prefix: 'far', iconName: 'file-music' },
			'Complete Your License',
			<LicenseCheckoutForm />
		);
	};

	return (
		<Stack spacing={1.5} sx={{ px: 1.5, py: 1.5 }}>
			<Stack direction="row" spacing={1} alignItems="center">
				<FontAwesomeIcon icon={['far', 'file-music']} />
				<Typography variant="body1">Purchase Zero-Attribution License</Typography>
			</Stack>
			<DividerGradient />
			{songsInCart.map(song => (
				<Stack
					key={`lic-song-${song.id}`}
					direction="row"
					spacing={2}
					justifyContent="space-between"
					alignItems="center"
				>
					<Typography
						variant="body2"
						sx={{
							minWidth: 0,
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis'
						}}
					>
						{song.title}
					</Typography>
					<IconButton size="small" onClick={() => removeSongFromCart(song.id)}>
						<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'times' }} fontSize={12} color={theme.palette.grey[700]} />
					</IconButton>
				</Stack>
			))}
			<Typography variant="body2" color="text.tertiary">{songsInCart.length} tune{songsInCart.length !== 1 ? 's' : ''} selected</Typography>
			<DividerGradient />
			<Stack direction="row" alignItems="flex-start" spacing={0.25}>
				<Typography variant="h6">${dollars}</Typography>
				<Typography variant="body2" color="textSecondary" sx={{ pt: 0.33 }}>.{centsRemainder}</Typography>
			</Stack>
			<Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
				<Button
					variant="contained"
					size="small"
					color="primary"
					disabled={!songsInCart.length}
					onClick={handleCheckoutClick}
				>
					License {songsInCart.length} Tune{songsInCart.length !== 1 ? 's' : ''}
				</Button>
				<Button
					variant="text"
					size="small"
					onClick={handleEmptyCartClick}
				>
					Empty Cart
				</Button>
			</Stack>
			<Typography variant="caption" color="textSecondary">
				You'll receive an email with your license PDF shortly after payment is confirmed.
			</Typography>
		</Stack>
	);
};

export default LicenseCart;
